import React from 'react';
import { ChevronRight } from 'lucide-react';
import { cn } from '../ui/utils';
import { Input } from '../ui/input';
import type { PerformanceGoal } from '../../types/performance.types';
import { formatPerformanceDate } from '../../utils/performance';
import { PerformancePill } from './PerformancePrimitives';

interface GoalCardProps {
  goal: PerformanceGoal;
  canEditWeight: boolean;
  weightMax?: number;
  onWeightChange?: (value: number) => void;
  onOpen: () => void;
  readOnly?: boolean;
}

/**
 * One goal row: category + status, title, cascade line, dates, weight and progress.
 * The weight input commits on blur, not per keystroke, so typing "25" never briefly
 * re-splits the other weights.
 */
export const GoalCard: React.FC<GoalCardProps> = ({
  goal,
  canEditWeight,
  weightMax,
  onWeightChange,
  onOpen,
  readOnly,
}) => {
  const withdrawn = goal.status === 'Withdrawn';
  const completed = goal.progress >= 100;
  const [draftWeight, setDraftWeight] = React.useState(String(goal.weight));

  React.useEffect(() => {
    setDraftWeight(String(goal.weight));
  }, [goal.weight]);

  return (
    <div
      className={cn(
        'group rounded-xl border border-border bg-card p-4 transition-shadow duration-200 hover:shadow-md',
        withdrawn && 'opacity-60'
      )}
    >
      <div className="flex items-start justify-between gap-4">
        <button
          type="button"
          onClick={onOpen}
          className="min-w-0 flex-1 text-left"
          aria-label={`Open ${goal.title}`}
        >
          <div className="flex flex-wrap items-center gap-2">
            <span
              className="h-2 w-2 shrink-0 rounded-full"
              style={{ backgroundColor: goal.categoryColour }}
            />
            <span className="text-xs text-muted-foreground">{goal.categoryName}</span>
            <PerformancePill
              tone={withdrawn ? 'slate' : completed ? 'success' : 'muted'}
            >
              {withdrawn ? 'Withdrawn' : completed ? 'Completed' : goal.status}
            </PerformancePill>
          </div>
          <h3 className="mt-1.5 text-sm font-semibold leading-snug text-foreground">
            {goal.title}
          </h3>
          <p className="mt-1 flex flex-wrap items-center gap-1 text-xs text-muted-foreground">
            <span className="text-link">↳</span>
            <span>{goal.departmentGoalTitle || 'Not aligned'}</span>
            {goal.companyGoalTitle ? (
              <>
                <span className="text-muted-foreground/60">·</span>
                <span>{goal.companyGoalTitle}</span>
              </>
            ) : null}
          </p>
          <p className="mt-1.5 text-xs tabular-nums text-muted-foreground">
            {formatPerformanceDate(goal.startDate)} – {formatPerformanceDate(goal.endDate)}
          </p>
        </button>

        <div className="flex shrink-0 flex-col items-end gap-1.5">
          <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
            Weight
          </span>
          {canEditWeight && !readOnly && !withdrawn ? (
            <div className="flex items-center gap-1">
              <Input
                type="number"
                min={0}
                max={weightMax ?? 100}
                value={draftWeight}
                onClick={(event) => event.stopPropagation()}
                onChange={(event) => setDraftWeight(event.target.value)}
                onBlur={() => {
                  const next = Number(draftWeight);
                  if (next !== goal.weight && Number.isFinite(next)) onWeightChange?.(next);
                  else setDraftWeight(String(goal.weight));
                }}
                className="h-7 w-16 text-right text-xs tabular-nums"
                aria-label={`Weight for ${goal.title}`}
              />
              <span className="text-xs text-muted-foreground">%</span>
            </div>
          ) : (
            <span className="text-sm font-semibold tabular-nums text-foreground">
              {goal.weight}%
            </span>
          )}
          <span className="text-xs tabular-nums text-muted-foreground">
            {goal.progress}%
          </span>
          <ChevronRight className="h-4 w-4 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
        </div>
      </div>

      <button type="button" onClick={onOpen} className="mt-3 block w-full text-left">
        <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
          <div
            className={cn('h-full rounded-full transition-all', completed ? 'bg-success' : 'bg-primary')}
            style={{ width: `${Math.min(100, goal.progress)}%` }}
          />
        </div>
        <div className="mt-1.5 flex items-center justify-between gap-2 text-xs text-muted-foreground">
          <span>
            {goal.progressMode === 'Target'
              ? `${goal.current} of ${goal.target} ${goal.unit}`
              : goal.progressMode === 'KeyResults'
                ? `${goal.keyResults.filter((kr) => kr.isDone).length}/${goal.keyResults.length} key results`
                : goal.progress + '% complete'}
          </span>
          <span className="tabular-nums">
            {goal.progressMode === 'Target' || goal.progressMode === 'KeyResults'
              ? `${goal.progress}%`
              : 'Percentage'}
          </span>
        </div>
      </button>
    </div>
  );
};
